/**
 * Helper functions for auto-validation processing
 * Extracted to reduce cognitive complexity and improve maintainability
 */
import { getDaysAgo, getFormattedISODate, getTodayLocalDateString } from "../../shared/Utils";
/**
 * Tax ID field name variations
 */
export const TAX_ID_KEYS = [
    "taxIds",
    "tax_id",
    "taxId",
    "taxpayerIds",
    "taxNumbers",
    "tax_ids",
] as const;

/**
 * Expiration date field name from the API
 */
export const EXPIRATION_DATE_KEY = "expirationDate";

/**
 * Check if a key is a tax ID field
 */
export const isTaxIdKey = (key: string): boolean =>
    TAX_ID_KEYS.includes(key as (typeof TAX_ID_KEYS)[number]);

/**
 * Check if a key is the expiration date field
 */
export const isExpirationDateKey = (key: string): boolean => key === EXPIRATION_DATE_KEY;

/**
 * Check if a date string represents a date in the past.
 */
export const isExpiredDate = (dateStr: string): boolean => getDaysAgo(dateStr) > 0;

interface RuleChecks {
    requiredCheck: Record<string, unknown>;
    optionalCheck: Record<string, unknown>;
}

export interface AcceptanceSkipFlags {
    identifier: boolean;
    effectiveDate: boolean;
    expirationDate: boolean;
    businessType: boolean;
    purchaseOrder: boolean;
}

/**
 * Concatenate tax ID entries while removing duplicates by `value`.
 *
 * Deduplication is intentionally by `value` only — the same tax ID number appearing
 * under different states (e.g. from optionalCheck with an empty state) is treated as
 * one entry. This avoids state-less duplicates from optionalCheck shadowing the
 * state-qualified requiredCheck entry; for multi-jurisdiction certs the upstream
 * caller handles state matching.
 */
const mergeTaxIdEntries = (existing: unknown[], incoming: unknown[]): unknown[] => {
    const result = [...existing];
    incoming.forEach(entry => {
        const value = (entry as Record<string, unknown>)?.value;
        const isDuplicate = result.some(
            existingEntry => (existingEntry as Record<string, unknown>)?.value === value
        );
        if (!isDuplicate) {
            result.push(entry);
        }
    });
    return result;
};

/**
 * Merge `optionalCheck` fields into `requiredCheck` so downstream parsing also captures
 * optional predictions (e.g. `businessType`) and optional tax IDs. Tax ID arrays from both
 * checks are concatenated; other optional fields only fill gaps left by the required check.
 */
export const mergeValidationChecks = (
    ruleProcessingResult: Record<string, unknown> | undefined
): Record<string, unknown> => {
    const requiredCheck = (ruleProcessingResult?.requiredCheck as Record<string, unknown>) ?? {};
    const optionalCheck = (ruleProcessingResult?.optionalCheck as Record<string, unknown>) ?? {};
    const merged: Record<string, unknown> = { ...requiredCheck };

    Object.keys(optionalCheck).forEach(key => {
        const optionalValue = optionalCheck[key];
        if (optionalValue === null || optionalValue === undefined) {
            return;
        }

        if (isTaxIdKey(key)) {
            const existing = Array.isArray(merged[key]) ? (merged[key] as unknown[]) : [];
            const incoming = Array.isArray(optionalValue) ? optionalValue : [optionalValue];
            merged[key] = mergeTaxIdEntries(existing, incoming);
        } else if (merged[key] === undefined || merged[key] === null) {
            merged[key] = optionalValue;
        }
    });

    return merged;
};

/**
 * Resolve the validation data object from an IDP `certificate-validation` response,
 * folding `optionalCheck` into `requiredCheck` when the rule-processing result is present.
 */
export const extractValidationDataFromIdpResponse = (
    response: Record<string, unknown>
): Record<string, unknown> => {
    const validationResults = response?.validationResults as
        | { ruleProcessingResult?: Record<string, unknown> }
        | undefined;
    const ruleProcessingResult = validationResults?.ruleProcessingResult;

    if (ruleProcessingResult?.requiredCheck || ruleProcessingResult?.optionalCheck) {
        return mergeValidationChecks(ruleProcessingResult);
    }
    if (validationResults) {
        return validationResults as Record<string, unknown>;
    }
    if (response?.requiredCheck) {
        return response.requiredCheck as Record<string, unknown>;
    }
    return response;
};

/**
 * Normalize a tax-ID-like value for comparison by stripping spaces, hyphens and
 * any other non-alphanumeric characters, then upper-casing it. e.g.
 * "604-967-838" -> "604967838", "14960369-003-STC" -> "14960369003STC".
 */
export const normalizeTaxId = (value: unknown): string =>
    String(value ?? "")
        .replace(/[^a-z0-9]/gi, "")
        .toUpperCase();

/**
 * Whether a predicted identifier represents an "exempt" placeholder. When true,
 * the predicted value must not be compared against the user-entered value
 * (users commonly enter NA / empty / 00000 for exempt identifiers).
 */
export const isExemptPrediction = (value: unknown): boolean =>
    String(value ?? "")
        .trim()
        .toLowerCase() === "exempt";

/**
 * Predicted identifier keys other than region-specific taxIds. The user may
 * enter any one of these into the tax number field, so they are valid match
 * candidates regardless of region.
 */
export const GLOBAL_PREDICTED_ID_KEYS = [
    "federalId",
    "accountNumber",
    "utilityAccountNumber",
    "parishAccountNumber",
    "fein",
] as const;

/**
 * Collect the non-empty global predicted identifiers from the predictions.
 */
export const buildGlobalPredictedIds = (
    predictions: Record<string, unknown> | undefined | null
): unknown[] =>
    GLOBAL_PREDICTED_ID_KEYS.map(key => predictions?.[key]).filter(
        id => id !== undefined && id !== null && String(id).trim() !== ""
    );

/**
 * Whether an IDP check field (object with `value`, array, or primitive) carries data.
 */
export const hasIdpCheckFieldValue = (field: unknown): boolean => {
    if (field === null || field === undefined) {
        return false;
    }
    if (Array.isArray(field)) {
        return field.length > 0;
    }
    if (typeof field === "object" && "value" in field) {
        const value = (field as { value: unknown }).value;
        return value !== null && value !== undefined && String(value).trim() !== "";
    }
    return String(field).trim() !== "";
};

/**
 * Whether a rule-processing check object includes any tax-ID field with data.
 */
export const hasTaxIdFieldData = (check: Record<string, unknown> | undefined | null): boolean => {
    if (!check) {
        return false;
    }
    return TAX_ID_KEYS.some(key => hasIdpCheckFieldValue(check[key]));
};

/**
 * Resolve the `requiredCheck` / `optionalCheck` objects from a rule-processing result,
 * defaulting each to an empty object so callers can index safely.
 */
const extractRuleChecks = (
    ruleProcessingResult: Record<string, unknown> | undefined | null
): RuleChecks => ({
    requiredCheck: (ruleProcessingResult?.requiredCheck as Record<string, unknown>) ?? {},
    optionalCheck: (ruleProcessingResult?.optionalCheck as Record<string, unknown>) ?? {},
});

/**
 * Whether a field exists only in optionalCheck (not in requiredCheck) on the IDP response.
 * Used by aiScannedAccepted to skip acceptance comparisons for optional predictions.
 */
export const isFieldOptionalOnly = (
    ruleProcessingResult: Record<string, unknown> | undefined | null,
    fieldKey: string
): boolean => {
    if (!ruleProcessingResult) {
        return false;
    }
    const { requiredCheck, optionalCheck } = extractRuleChecks(ruleProcessingResult);

    // Tax IDs span several key variations, so check them as a group.
    if (isTaxIdKey(fieldKey)) {
        return hasTaxIdFieldData(optionalCheck) && !hasTaxIdFieldData(requiredCheck);
    }

    return (
        hasIdpCheckFieldValue(optionalCheck[fieldKey]) &&
        !hasIdpCheckFieldValue(requiredCheck[fieldKey])
    );
};

/**
 * Whether aiScannedAccepted should skip the identifier (tax ID) comparison.
 *
 * Tax ID is the gatekeeping identifier: its comparison is enforced only when a
 * tax ID was predicted in requiredCheck. When requiredCheck has no tax ID — i.e.
 * tax IDs are empty in both checks, or present only in optionalCheck — the
 * comparison is skipped. Other global identifiers (federalId, account numbers,
 * etc.) do not influence this decision; they only serve as additional acceptable
 * candidates when the comparison does run.
 */
export const shouldSkipIdentifierAcceptanceCheck = (
    ruleProcessingResult: Record<string, unknown> | undefined | null
): boolean => {
    if (!ruleProcessingResult) {
        return false;
    }
    const { requiredCheck } = extractRuleChecks(ruleProcessingResult);
    return !hasTaxIdFieldData(requiredCheck);
};

/**
 * Resolve, in a single pass, which aiScannedAccepted comparisons to skip because the
 * corresponding prediction was present only in optionalCheck.
 */
export const getAcceptanceSkipFlags = (
    ruleProcessingResult: Record<string, unknown> | undefined | null
): AcceptanceSkipFlags => ({
    identifier: shouldSkipIdentifierAcceptanceCheck(ruleProcessingResult),
    effectiveDate: isFieldOptionalOnly(ruleProcessingResult, "effectiveDate"),
    expirationDate: isFieldOptionalOnly(ruleProcessingResult, "expirationDate"),
    businessType: isFieldOptionalOnly(ruleProcessingResult, "businessType"),
    purchaseOrder: isFieldOptionalOnly(ruleProcessingResult, "purchaseOrder"),
});

interface RegionEntry {
    taxId?: unknown;
    candidates: unknown[];
}

/**
 * Add a region entry to the map, but only when the state resolves to a region
 * id; an unresolved (null/undefined) state must not create an "undefined" key.
 */
const addRegionEntry = (
    map: Record<string, RegionEntry>,
    extractRegionIds: (state: unknown) => unknown,
    globalPredictedIds: unknown[],
    regionState: unknown,
    taxIdValue: unknown
): void => {
    const regionId = extractRegionIds(regionState);
    if (regionId === undefined || regionId === null) {
        return;
    }
    map[regionId as string] = {
        taxId: taxIdValue,
        candidates: [taxIdValue, ...globalPredictedIds],
    };
};

/**
 * Seed each posted certificate's region with the given identifier candidates so
 * the acceptance loop runs once per saved certificate. Certificates without an
 * exposure zone are ignored (they cannot be matched against any prediction).
 */
const seedCertificateRegions = (
    map: Record<string, RegionEntry>,
    certificates: Array<{ exposureZoneId?: unknown }> | undefined | null,
    candidates: unknown[]
): void => {
    certificates?.forEach(cert => {
        if (cert?.exposureZoneId !== undefined && cert?.exposureZoneId !== null) {
            map[cert.exposureZoneId as string] = {
                taxId: undefined,
                candidates: [...candidates],
            };
        }
    });
};

/**
 * Build a map of region (exposureZone) id -> predicted taxId and the full list
 * of acceptable identifier candidates for that region. When no region-specific
 * taxIds are predicted but global identifiers exist, each certificate's region
 * is seeded so the acceptance loop still runs and can match those identifiers.
 * When the AI attempted a tax ID but extracted no value (taxIds present yet all
 * empty/errored) and no global identifiers exist, each region is seeded with an
 * empty candidate so the loop accepts the certificate only when the user also
 * left the tax number empty.
 */
export const buildRegionCandidateMap = ({
    predictions,
    isMulti,
    globalPredictedIds,
    certificates,
    extractRegionIds,
}: {
    predictions: Record<string, unknown> | undefined | null;
    isMulti: boolean | undefined;
    globalPredictedIds: unknown[];
    certificates: Array<{ exposureZoneId?: unknown }> | undefined | null;
    extractRegionIds: (state: unknown) => unknown;
}): Record<string, RegionEntry> => {
    const map: Record<string, RegionEntry> = {};
    const taxIds = predictions?.taxIds as Array<{ value?: unknown; state?: unknown }> | undefined;
    let state = predictions?.state;

    if (isMulti && taxIds?.length === 1) {
        if (!state && taxIds[0]?.state) {
            state = taxIds[0].state;
        }
        addRegionEntry(map, extractRegionIds, globalPredictedIds, state, taxIds[0]?.value);
    } else if (isMulti && taxIds && taxIds.length > 1) {
        taxIds.forEach(entry =>
            addRegionEntry(map, extractRegionIds, globalPredictedIds, entry?.state, entry?.value)
        );
    } else if (!isMulti && taxIds?.length && taxIds[0]?.value) {
        addRegionEntry(map, extractRegionIds, globalPredictedIds, state, taxIds[0]?.value);
    }

    if (Object.keys(map).length === 0 && globalPredictedIds.length > 0) {
        seedCertificateRegions(map, certificates, globalPredictedIds);
    } else if (Object.keys(map).length === 0 && globalPredictedIds.length === 0) {
        // The AI attempted a tax ID (taxIds present) but extracted no usable
        // value, so there are no region predictions to match. Seed each saved
        // region with an empty candidate so the acceptance loop still runs and
        // accepts only when the user likewise left the tax number empty.
        const taxIdAttemptedButEmpty =
            Array.isArray(taxIds) &&
            taxIds.length > 0 &&
            !taxIds.some(entry => String(entry?.value ?? "").trim() !== "");
        if (taxIdAttemptedButEmpty) {
            seedCertificateRegions(map, certificates, [""]);
        }
    }
    return map;
};

/**
 * Whether the user-entered tax number is accepted for a region. The comparison
 * is skipped (accepted) only when the predicted taxId value itself is "exempt";
 * otherwise the normalized user value must match any predicted candidate.
 */
export const isIdentifierAccepted = (
    regionEntry: Partial<RegionEntry> | undefined,
    userTaxNumber: unknown
): boolean => {
    if (isExemptPrediction(regionEntry?.taxId)) {
        return true;
    }
    const userTaxId = normalizeTaxId(userTaxNumber);
    return (regionEntry?.candidates ?? []).some(
        candidate => normalizeTaxId(candidate) === userTaxId
    );
};

/**
 * Whether the predicted effective date is accepted. Predicted dates may arrive
 * as MM/DD/YYYY while the certificate's dates are ISO (YYYY-MM-DD), so the
 * predicted value is normalized to ISO before comparing. Acceptance also passes
 * when the user opted for today's date even if the predicted date differs.
 */
export const isEffectiveDateAccepted = (
    predictedEffectiveDate: unknown,
    certificateSignedDate: unknown
): boolean => {
    if (!predictedEffectiveDate) {
        return true;
    }
    const effDate = certificateSignedDate ? String(certificateSignedDate).split("T")[0] : "";
    const predictedIso =
        getFormattedISODate(predictedEffectiveDate, true) ?? predictedEffectiveDate;
    const userOptedTodayDate = effDate === getTodayLocalDateString();
    return userOptedTodayDate || predictedIso === effDate;
};

/**
 * Whether the predicted expiration date is accepted. Skipped (accepted) for
 * multi-certificates or when no expiration date was predicted. The predicted
 * value is normalized to ISO before comparing.
 */
export const isExpirationDateAccepted = (
    predictedExpirationDate: unknown,
    certificateExpirationDate: unknown,
    isMulti: boolean | undefined
): boolean => {
    if (isMulti || !predictedExpirationDate) {
        return true;
    }
    const expDate = certificateExpirationDate
        ? String(certificateExpirationDate).split("T")[0]
        : "";
    const predictedIso =
        getFormattedISODate(predictedExpirationDate, true) ?? predictedExpirationDate;
    return predictedIso === expDate;
};

/**
 * Process signatures field and add to issues if empty
 */
export const processSignaturesField = (value: boolean, issues: string[]): void => {
    if (value) {
        issues.push("Signature");
    }
};

/**
 * Process a single tax ID entry
 */
export const processTaxId = (
    taxId: Record<string, unknown>,
    issues: string[],
    predictions: Record<string, unknown>,
    hasTaxFormatError: { value: boolean }
): void => {
    if (taxId.isError) {
        issues.push("Tax ID");
        if (taxId.value && typeof taxId.value === "string" && taxId.value.length > 0) {
            hasTaxFormatError.value = true;
        }
        // Only add to predictions if it has a value
        if ("value" in taxId) {
            if (!("taxIds" in predictions)) predictions.taxIds = [];
            (predictions.taxIds as unknown[]).push(taxId);
        }
    } else {
        // Always store predictions for mismatch detection
        if (!("taxIds" in predictions)) predictions.taxIds = [];
        (predictions.taxIds as unknown[]).push(taxId);
    }
};

/**
 * Process tax IDs field (handles both array and single object)
 */
export const processTaxIdsField = (
    value: unknown,
    issues: string[],
    predictions: Record<string, unknown>,
    hasTaxFormatError: { value: boolean }
): void => {
    const taxIdsArray = Array.isArray(value) ? value : [value];
    taxIdsArray.forEach(taxId =>
        processTaxId(taxId as Record<string, unknown>, issues, predictions, hasTaxFormatError)
    );
};

/**
 * Process field with error flag
 */
export const processErrorField = (
    key: string,
    value: Record<string, unknown>,
    issues: string[],
    missingFields: string[]
): void => {
    if (value.reasonPhrase) {
        issues.push(value.reasonPhrase as string);
    } else {
        missingFields.push(key);
    }
};

/**
 * Detects an optional (non-error) field that has no value but reports a
 * reasonPhrase — i.e. a null optional field that should still be surfaced in
 * the missing-details banner.
 */
export const isMissingValueWithReason = (value: Record<string, unknown>): boolean => {
    if (value.isError || !("value" in value)) return false;
    const fieldValue = value.value;
    const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === "";
    return isEmpty && typeof value.reasonPhrase === "string" && value.reasonPhrase.length > 0;
};

/**
 * Process expiration date field — checks for isError and whether the date is in the past
 */
export const processExpirationDateField = (
    key: string,
    value: Record<string, unknown>,
    issues: string[],
    predictions: Record<string, unknown>,
    missingFields: string[],
    hasExpiredDate: { value: boolean }
): void => {
    if (value.isError) {
        processErrorField(key, value, issues, missingFields);
        return;
    }
    if ("value" in value && typeof value.value === "string" && value.value.length > 0) {
        predictions[key] = value.value;
        if (isExpiredDate(value.value)) {
            hasExpiredDate.value = true;
            issues.push("Expiration Date");
        }
    }
};

/**
 * Process field with value property
 */
export const processValueField = (
    key: string,
    value: Record<string, unknown>,
    predictions: Record<string, unknown>
): void => {
    predictions[key] = value.value;
};

/**
 * Process primitive field value
 */
export const processPrimitiveField = (
    key: string,
    value: unknown,
    predictions: Record<string, unknown>
): void => {
    predictions[key] = value;
};

/**
 * Process a single validation field
 */
export const processValidationField = (
    key: string,
    value: unknown,
    issues: string[],
    predictions: Record<string, unknown>,
    missingFields: string[],
    hasTaxFormatError: { value: boolean },
    hasExpiredDate: { value: boolean }
): void => {
    if (value === null) {
        return; // Skip null values
    }

    if (key === "signaturesIsError") {
        processSignaturesField(value as boolean, issues);
    } else if (isTaxIdKey(key)) {
        processTaxIdsField(value, issues, predictions, hasTaxFormatError);
    } else if (isExpirationDateKey(key) && value && typeof value === "object") {
        processExpirationDateField(
            key,
            value as Record<string, unknown>,
            issues,
            predictions,
            missingFields,
            hasExpiredDate
        );
    } else if (value && typeof value === "object" && (value as Record<string, unknown>).isError) {
        processErrorField(key, value as Record<string, unknown>, issues, missingFields);
    } else if (
        value &&
        typeof value === "object" &&
        isMissingValueWithReason(value as Record<string, unknown>)
    ) {
        issues.push((value as Record<string, unknown>).reasonPhrase as string);
    } else if (value && typeof value === "object" && "value" in value) {
        processValueField(key, value as Record<string, unknown>, predictions);
    } else if (value && typeof value !== "object") {
        processPrimitiveField(key, value, predictions);
    }
};

/**
 * Maps raw validation field keys (and already-friendly issue labels) to the
 * display labels shown in the AVS missing-fields banner. Keys are normalised
 * to lower-case before lookup.
 */
export const FIELD_LABELS: Record<string, string> = {
    taxids: "Tax ID",
    taxid: "Tax ID",
    tax_id: "Tax ID",
    tax_ids: "Tax ID",
    taxpayerids: "Tax ID",
    taxnumbers: "Tax ID",
    purchasername: "Purchaser name",
    sellername: "Seller name",
    state: "Regions covered by this certificate",
    exposurezone: "Regions covered by this certificate",
    region: "Regions covered by this certificate",
    regions: "Regions covered by this certificate",
    expirationdate: "Expiration date",
    effectivedate: "Effective date",
    signeddate: "Effective date",
    reason: "Exemption reason",
    signatures: "Signature",
    signaturesiserror: "Signature",
};

/**
 * Convert a single-token field key (camelCase, PascalCase, snake_case or
 * kebab-case) into Title Case, space-separated words. e.g.
 * `singleOrBlanketCheckbox` -> `Single Or Blanket Checkbox`, `typeOfTax` -> `Type Of Tax`.
 * Strings that already contain a space are assumed to be human-readable
 * (e.g. an API reasonPhrase like "Purchaser Name") and are returned unchanged.
 */
export const humanizeFieldKey = (key: string): string => {
    if (key.includes(" ")) return key;
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

/**
 * Resolve a friendly display label for a field key. Known keys use the
 * curated FIELD_LABELS mapping; everything else is humanised so camelCase
 * keys (e.g. `typeOfTax`) render as readable words ("type of tax"). Strings
 * that are already human-readable (multi-word reasonPhrases) are preserved.
 */
export const getFieldLabel = (key: string): string => {
    if (!key) return key;
    return FIELD_LABELS[key.trim().toLowerCase()] ?? humanizeFieldKey(key.trim());
};

/**
 * Extract quoted field names from a combined "missing" message such as
 * `'reason', 'expirationDate' are missing in your certificate`.
 */
const extractQuotedFields = (text: string): string[] => {
    const matches = text.match(/'([^']+)'/g);
    return matches ? matches.map(match => match.replace(/'/g, "")) : [];
};

/**
 * Build the de-duplicated list of friendly missing-field labels.
 *
 * Labels that originate from a reasonPhrase (or other already-friendly issue
 * string) are used as provided — e.g. `fein` reports reasonPhrase "Fein". Raw
 * field keys without a reasonPhrase (collected in `missingFields` and the
 * combined "... is missing" message) are mapped or humanised so camelCase keys
 * such as `typeOfTax` render as "type of tax".
 */
export const getMissingFieldLabels = (
    issues: string[] = [],
    missingFields: string[] = []
): string[] => {
    const labels: string[] = [];

    (missingFields ?? []).forEach(key => labels.push(getFieldLabel(key)));

    (issues ?? []).forEach(issue => {
        if (typeof issue !== "string") return;
        if (/missing/i.test(issue)) {
            extractQuotedFields(issue).forEach(key => labels.push(getFieldLabel(key)));
        } else {
            labels.push(FIELD_LABELS[issue.trim().toLowerCase()] ?? issue);
        }
    });

    return [...new Set(labels)];
};

/**
 * Format missing fields into a human-readable message
 */
export const formatMissingFieldsMessage = (missingFields: string[]): string => {
    const quotedFields = missingFields.map(key => `'${key}'`);

    if (quotedFields.length === 1) {
        return `${quotedFields[0]} is missing in your certificate`;
    }

    const lastField = quotedFields[quotedFields.length - 1];
    const remainingFields = quotedFields.slice(0, -1);
    return `${remainingFields.join(", ")} and ${lastField} are missing in your certificate`;
};

/**
 * Process all validation results and extract issues and predictions
 */
export const processValidationResults = (autoValidationResults: Record<string, unknown>) => {
    const issues: string[] = [];
    const predictions: Record<string, unknown> = {};
    const missingFields: string[] = [];
    const hasTaxFormatError = { value: false };
    const hasExpiredDate = { value: false };

    // Clean up null values and process each field
    Object.keys(autoValidationResults).forEach(key => {
        const value = autoValidationResults[key];
        if (value === null) {
            delete autoValidationResults[key];
        } else {
            processValidationField(
                key,
                value,
                issues,
                predictions,
                missingFields,
                hasTaxFormatError,
                hasExpiredDate
            );
        }
    });

    // Add missing fields message if any
    if (missingFields.length > 0) {
        issues.push(formatMissingFieldsMessage(missingFields));
    }

    return {
        issues,
        predictions,
        missingFields,
        hasTaxFormatError: hasTaxFormatError.value,
        hasExpiredDate: hasExpiredDate.value,
    };
};

/**
 * Create validation data object
 */
export const createValidationData = (
    issues: string[],
    predictions: Record<string, unknown>,
    isMulti: boolean | undefined,
    isSinglePurchase: boolean | undefined,
    type: string,
    hasTaxFormatError: boolean,
    hasExpiredDate: boolean = false,
    missingFields: string[] = []
) => ({
    status: issues.length > 0 ? "warning" : "success",
    issues,
    predictions,
    missingFields,
    isMulti,
    isSinglePurchase,
    taskType: type,
    isInValidTaxFormat: hasTaxFormatError,
    isExpiredDate: hasExpiredDate,
});

/**
 * Handle invalid validation data
 */
export const handleInvalidData = (
    dispatch: (action: unknown) => void,
    type: string,
    onlyParse: boolean,
    setIsAutoValidatingFile: (value: boolean) => unknown,
    setAutoValidationData: (data: unknown) => unknown
): void => {
    if (!onlyParse) {
        dispatch(setIsAutoValidatingFile(false));
        dispatch(
            setAutoValidationData({
                status: "error",
                issues: ["Invalid validation data received"],
                predictions: {},
                isMulti: false,
                isSinglePurchase: false,
                taskType: type,
                isInValidTaxFormat: false,
            })
        );
    }
};
